/**
 * ルームIDの生成・検証・解決。
 *
 * ルームIDは `crypto.randomUUID()` を base64url 化した22文字。**短くしない。**
 * 認証が無く「IDを知っていれば入れる」設計なので、推測されにくさがそのまま
 * アクセス制御になる。→ doc/service/master.md#ルームid
 *
 * Phase 2（Discord Activity）では `instanceId` がルームIDになる。差分を数十行に
 * するため、**ルームIDの決定はこのファイルの resolveRoomId() 1か所に閉じる。**
 */

import { IS_ACTIVITY, PROXY_WS } from './discord'

export const ROOM_ID_RE = /^[A-Za-z0-9_-]{22}$/

export function isRoomId(v: unknown): boolean {
  return typeof v === 'string' && ROOM_ID_RE.test(v)
}

/** UUID(16バイト) を base64url にすると padding を除いて22文字になる */
export function newRoomId(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * 入力からルームIDを拾う。URL 全体を貼られても拾えるようにする
 * （共有バーのURLをそのまま貼る人がいるため）。不正なら null。
 */
export function parseRoomId(input: string): string | null {
  const s = input.trim()
  if (!s) return null
  if (isRoomId(s)) return s
  const m = s.match(/\/r\/([A-Za-z0-9_-]{22})/)
  return m ? m[1] : null
}

/** いまの URL が指しているルーム。`/r/<id>` 以外なら null（＝トップ画面） */
export function roomIdFromPath(pathname: string = location.pathname): string | null {
  const m = pathname.match(/^\/r\/([^/]+)\/?$/)
  if (!m) return null
  return isRoomId(m[1]) ? m[1] : null
}

/** `/r/<id>` の形式ではあるが ID が不正、という状態を画面に出すために使う */
export function hasBadRoomIdInPath(pathname: string = location.pathname): boolean {
  const m = pathname.match(/^\/r\/([^/]+)\/?$/)
  return Boolean(m) && !isRoomId(m![1])
}

/**
 * このクライアントが入るルームID。
 * Phase 2 ではここで Embedded App SDK の instanceId を返すようにする。
 */
export async function resolveRoomId(): Promise<string | null> {
  return roomIdFromPath()
}

/** ルーム画面へ遷移する（履歴を積む。戻るでトップに戻れる） */
export function gotoRoom(roomId: string) {
  history.pushState(null, '', `/r/${roomId}`)
  dispatchEvent(new PopStateEvent('popstate'))
}

export function gotoHome() {
  history.pushState(null, '', '/')
  dispatchEvent(new PopStateEvent('popstate'))
}

/** WebSocket の接続先。開発時は Vite(5173) 経由、本番は Pages → Worker */
export function roomWsUrl(roomId: string): string {
  // Discord Activity の中では workers.dev を直接叩けない（CSP がプロキシドメインに
  // 閉じる）。**VITE_WS_ORIGIN より先に見る。** Activity も Pages と同じバンドルを
  // 読むので、後ろに置くとビルド時に埋まった workers.dev のほうが勝ってしまう
  if (IS_ACTIVITY) return `wss://${location.host}${PROXY_WS}/room/${roomId}`

  const base = import.meta.env.VITE_WS_ORIGIN as string | undefined
  if (base) return `${base.replace(/\/$/, '')}/room/${roomId}`
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}/room/${roomId}`
}

/* ---- 最近のルーム（この端末にだけ残る。共有はしない） ---- */

const LS_RECENT = 'tm.recentRooms'
const RECENT_MAX = 5

export type RecentRoom = { id: string; at: number }

export function recentRooms(): RecentRoom[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_RECENT) ?? '[]')
    if (!Array.isArray(raw)) return []
    return raw
      .filter((r) => r && isRoomId(r.id) && typeof r.at === 'number')
      .slice(0, RECENT_MAX)
  } catch {
    return []
  }
}

export function rememberRoom(id: string) {
  if (!isRoomId(id)) return
  const next = [{ id, at: Date.now() }, ...recentRooms().filter((r) => r.id !== id)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(LS_RECENT, JSON.stringify(next))
  } catch {
    // プライベートウィンドウなどで書けなくても、ルームそのものは使える
  }
}

export function forgetRoom(id: string) {
  try {
    localStorage.setItem(LS_RECENT, JSON.stringify(recentRooms().filter((r) => r.id !== id)))
  } catch {
    /* 同上 */
  }
}
